import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { useEffect, useRef } from 'react'
import { JustSpinner } from 'components/Loading/Loading'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { ISiteNetworkDraftFeature, ISiteNetworkGraphFeature } from 'types/api/siteNetworkApi.interface'
import { INetworkMapProps } from 'types/siteNetwork.interface'
import { RoadClass } from 'types/siteNetworkEnums.interface'
import { defaultRoadClassColor, roadClassColors, roadDraftColor } from './siteNetworkData'
import { MapLoading, MapShell } from './NetworkMap.styled'

const mapOptions: google.maps.MapOptions = {
  gestureHandling: 'greedy',
  zoom: 3,
  center: { lat: 0, lng: 0 },
  backgroundColor: tokens.surface.raised,
  disableDefaultUI: true,
  zoomControl: true,
  disableDoubleClickZoom: true,
  mapTypeId: 'satellite',
}

const toLatLng = (coord: number[]): google.maps.LatLngLiteral => ({ lat: coord[1], lng: coord[0] })

const toPath = (coordinates: number[][]): google.maps.LatLngLiteral[] => coordinates.map(toLatLng)

const dashIcons = (color: string, weight: number): google.maps.IconSequence[] => [
  {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      strokeColor: color,
      strokeWeight: weight,
      scale: 3,
    },
    offset: '0',
    repeat: '16px',
  },
]

const lineStrings = (graph: INetworkMapProps['graph']): ISiteNetworkGraphFeature[] =>
  (graph?.features ?? []).filter((f) => f.geometry.type === 'LineString')

const points = (graph: INetworkMapProps['graph']): ISiteNetworkGraphFeature[] =>
  (graph?.features ?? []).filter((f) => f.geometry.type === 'Point')

const classWeight = (roadClass: string | number): number => {
  const n = Number(roadClass)
  if (n === RoadClass.HaulPrimary || roadClass === 'HAUL_PRIMARY') return 5
  if (n === RoadClass.HaulRamp || roadClass === 'HAUL_RAMP') return 4.5
  return 3
}

const NetworkMapInner = ({
  graph,
  draftFeatures = [],
  closedSegmentIds = [],
  routeGeometry,
  proposalGeometry,
  drawCoordinates = [],
  geofences = [],
  fitCoordinates = [],
  pickCoordinates,
  mode = 'view',
  onSegmentClick,
  onMapClick,
  onMapDoubleClick,
}: INetworkMapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const overlaysRef = useRef<google.maps.MVCObject[]>([])
  const listenersRef = useRef<google.maps.MapsEventListener[]>([])
  const fittedRef = useRef(false)

  const modeRef = useRef(mode)
  const onSegmentClickRef = useRef(onSegmentClick)
  const onMapClickRef = useRef(onMapClick)
  const onMapDoubleClickRef = useRef(onMapDoubleClick)

  modeRef.current = mode
  onSegmentClickRef.current = onSegmentClick
  onMapClickRef.current = onMapClick
  onMapDoubleClickRef.current = onMapDoubleClick

  const clearOverlays = () => {
    overlaysRef.current.forEach((overlay) => {
      if ('setMap' in overlay && typeof overlay.setMap === 'function') {
        overlay.setMap(null)
      }
    })
    overlaysRef.current = []
  }

  const clearListeners = () => {
    listenersRef.current.forEach((listener) => listener.remove())
    listenersRef.current = []
  }

  useEffect(() => {
    if (!ref.current || mapRef.current) return
    mapRef.current = new google.maps.Map(ref.current, mapOptions)

    const clickListener = mapRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onMapClickRef.current?.([e.latLng.lng(), e.latLng.lat()])
      }
    })
    const dblListener = mapRef.current.addListener('dblclick', (e: google.maps.MapMouseEvent) => {
      if (modeRef.current === 'draw' && e.latLng) {
        e.stop?.()
        onMapDoubleClickRef.current?.([e.latLng.lng(), e.latLng.lat()])
      }
    })
    listenersRef.current = [clickListener, dblListener]

    return clearListeners
  }, [])

  const closedKey = closedSegmentIds.join(',')
  const drawKey = drawCoordinates.map((c) => c.join(',')).join(';')
  const draftKey = draftFeatures.map((d) => d.id).join(',')
  const geofenceKey = geofences.map((g) => g.id).join(',')
  const pickKey = `${pickCoordinates?.origin?.join(',') ?? ''}|${pickCoordinates?.destination?.join(',') ?? ''}`
  const graphKey = `${graph?.features?.length ?? 0}-${points(graph).length}-${lineStrings(graph).length}`
  const fitKey = fitCoordinates.map((c) => c.join(',')).join(';')

  useEffect(() => {
    fittedRef.current = false
  }, [graphKey, geofenceKey, fitKey])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    clearOverlays()

    const segments = lineStrings(graph)
    const isInteractive = mode === 'view'

    if (!fittedRef.current) {
      const bounds = new google.maps.LatLngBounds()
      segments.forEach((f) =>
        (f.geometry.coordinates as number[][]).forEach((c) => bounds.extend(toLatLng(c))),
      )
      points(graph).forEach((f) => {
        const coords = f.geometry.coordinates as number[]
        if (coords?.length >= 2) bounds.extend(toLatLng(coords))
      })
      draftFeatures.forEach((d) => d.coordinates.forEach((c) => bounds.extend(toLatLng(c))))
      geofences.forEach((g) => {
        const coords = g.geometry?.coordinates as any
        if (!coords) return
        if (g.geometry?.type === 'Polygon') {
          const ring = (Array.isArray(coords[0]) ? coords[0] : []) as number[][]
          ring.forEach((c) => {
            if (c?.length >= 2) bounds.extend(toLatLng(c))
          })
        } else if (Array.isArray(coords)) {
          coords.forEach((c: number[]) => {
            if (Array.isArray(c) && typeof c[0] === 'number' && c.length >= 2) {
              bounds.extend(toLatLng(c))
            }
          })
        }
      })
      fitCoordinates.forEach((c) => {
        if (c?.length >= 2) bounds.extend(toLatLng(c))
      })
      drawCoordinates.forEach((c) => bounds.extend(toLatLng(c)))
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 60)
        fittedRef.current = true
      }
    }

    segments.forEach((feature) => {
      const id = String(feature.properties?.id ?? '')
      const isClosed =
        closedSegmentIds.includes(id) ||
        Boolean(feature.properties?.closed) ||
        feature.properties?.status === 3
      const roadClass = feature.properties?.class ?? ''
      const color = isClosed
        ? tokens.status.danger.fg
        : roadClassColors[String(roadClass)] ?? defaultRoadClassColor
      const path = toPath(feature.geometry.coordinates as number[][])

      const polyline = new google.maps.Polyline({
        path,
        map,
        strokeColor: color,
        strokeOpacity: isClosed ? 0 : 0.95,
        strokeWeight: classWeight(roadClass),
        icons: isClosed ? dashIcons(tokens.status.danger.fg, 4) : undefined,
        clickable: isInteractive,
        zIndex: 2,
      })
      if (isInteractive) {
        polyline.addListener('click', () =>
          onSegmentClickRef.current?.(id, (feature.properties?.name as string) ?? null),
        )
        polyline.addListener('mouseover', () => map.setOptions({ draggableCursor: 'pointer' }))
        polyline.addListener('mouseout', () => map.setOptions({ draggableCursor: '' }))
      }
      overlaysRef.current.push(polyline)
    })

    points(graph).forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates as number[]
      const kind = feature.properties?.kind
      const isAccess = kind === 'ACCESS_POINT' || kind === 2
      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: isAccess ? 6 : 3,
          fillColor: isAccess ? tokens.status.warning.fg : tokens.text.muted,
          fillOpacity: 1,
          strokeColor: tokens.accent.ink,
          strokeWeight: 1.5,
        },
        clickable: false,
        zIndex: 1,
      })
      overlaysRef.current.push(marker)
    })

    draftFeatures.forEach((draft: ISiteNetworkDraftFeature) => {
      const draftLine = new google.maps.Polyline({
        path: toPath(draft.coordinates),
        map,
        strokeColor: roadDraftColor,
        strokeOpacity: 0.45,
        strokeWeight: 2,
        icons: dashIcons(roadDraftColor, 3),
        clickable: false,
        zIndex: 5,
      })
      overlaysRef.current.push(draftLine)
    })

    geofences.forEach((geofence) => {
      const ring = geofence.geometry?.coordinates?.[0]
      if (!ring?.length) return
      const ringPath = toPath(ring)
      const isSpeed = Number(geofence.kind) === 4
      const color = isSpeed ? tokens.status.warning.fg : tokens.status.info.fg
      const polygon = new google.maps.Polygon({
        paths: ringPath,
        map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.12,
        clickable: false,
        zIndex: 2,
      })
      overlaysRef.current.push(polygon)
    })

    if (routeGeometry?.coordinates?.length) {
      const routeLine = new google.maps.Polyline({
        path: toPath(routeGeometry.coordinates),
        map,
        strokeColor: tokens.status.warning.fg,
        strokeOpacity: 0.95,
        strokeWeight: 5,
        zIndex: 4,
      })
      overlaysRef.current.push(routeLine)
    }

    if (proposalGeometry?.coordinates) {
      const proposalLine = new google.maps.Polyline({
        path: toPath(proposalGeometry.coordinates as number[][]),
        map,
        strokeColor: tokens.status.success.fg,
        strokeOpacity: 0,
        icons: dashIcons(tokens.status.success.fg, 4),
        clickable: false,
        zIndex: 4,
      })
      overlaysRef.current.push(proposalLine)
    }

    const addPick = (coord: number[] | null | undefined, color: string) => {
      if (!coord || coord.length < 2) return
      const marker = new google.maps.Marker({
        position: { lat: coord[1], lng: coord[0] },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: tokens.text.primary,
          strokeWeight: 2,
        },
        clickable: false,
        zIndex: 7,
      })
      overlaysRef.current.push(marker)
    }
    addPick(pickCoordinates?.origin, tokens.status.success.fg)
    addPick(pickCoordinates?.destination, tokens.accent.base)

    if (drawCoordinates.length) {
      if (drawCoordinates.length > 1) {
        const drawLine = new google.maps.Polyline({
          path: toPath(drawCoordinates),
          map,
          strokeColor: roadDraftColor,
          strokeOpacity: 0.9,
          strokeWeight: 3,
          zIndex: 5,
        })
        overlaysRef.current.push(drawLine)
      }
      drawCoordinates.forEach((coord) => {
        const vertex = new google.maps.Marker({
          position: toLatLng(coord),
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: roadDraftColor,
            fillOpacity: 1,
            strokeColor: tokens.text.primary,
            strokeWeight: 1,
          },
          clickable: false,
          zIndex: 6,
        })
        overlaysRef.current.push(vertex)
      })
    }

    return clearOverlays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, draftKey, geofenceKey, closedKey, routeGeometry, proposalGeometry, drawKey, pickKey, mode])

  return <MapShell ref={ref} data-testid='sn-network-map' />
}

export const NetworkMap = (props: INetworkMapProps) => (
  <Wrapper
    apiKey={process.env.REACT_APP_GOOGLE_KEY}
    version='beta'
    libraries={['marker']}
    render={(status) =>
      status === Status.SUCCESS ? (
        <NetworkMapInner {...props} />
      ) : (
        <MapLoading>
          {status === Status.FAILURE ? (
            <OtherText grey>Map failed to load</OtherText>
          ) : (
            <JustSpinner />
          )}
        </MapLoading>
      )
    }
  />
)
